import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import Image from 'next/image';
import { useKeenSlider } from 'keen-slider/react';

import { useTranslation } from '@hooks';

const ProductCardSlider = ({ product, priority, listingType }) => {
    const { locale } = useTranslation();
    const [currentSlide, setCurrentSlide] = useState(0);
    const [activeDot, setActiveDot] = useState(0);
    const [loaded, setLoaded] = useState([]);
    const [sliderRef, instanceRef] = useKeenSlider({
        initial: currentSlide,
        slidesPerView: 1,
        centered: true,
        loop: true,
        spacing: 10,
        renderMode: 'performance',
        slideChanged(s) {
            setActiveDot(s.track.details.rel);
        },
        animationEnded(s) {
            setCurrentSlide(s.track.details.rel);
        },
    });
    useEffect(() => {
        instanceRef?.current?.update();
    }, [listingType, product]);

    useEffect(() => {
        const newLoaded = [...loaded];
        newLoaded[currentSlide] = true;
        setLoaded(newLoaded);
    }, [currentSlide]);
    return (
        <ImageWrapper ref={sliderRef} className="keen-slider">
            {product.images.slice(0, 3).map((image, idx) => (
                <ImageContainer
                    key={idx} // eslint-disable-line
                    className="keen-slider__slide"
                    listingType={listingType}
                >
                    <Image
                        src={image.urls.large}
                        alt={product.slug[locale]}
                        placeholder="blur"
                        blurDataURL="/blurhash.png"
                        fill
                        quality={70}
                        loading={priority && idx === 0 ? 'eager' : 'lazy'}
                        priority={priority && idx === 0}
                        sizes="(max-width: 768px) 50vw,
                        (max-width: 1200px) 25vw"
                    />
                </ImageContainer>
            ))}
            {loaded && instanceRef.current && (
                <Dots>
                    {[
                        ...Array(
                            instanceRef.current?.track?.details?.slides?.length,
                        ).keys(),
                    ].map((idx) => (
                        <Dot
                            aria-label="slider-dot"
                            key={idx}
                            onClick={(e) => {
                                instanceRef.current?.moveToIdx(idx);
                                e.preventDefault();
                            }}
                            isActive={activeDot === idx}
                        />
                    ))}
                </Dots>
            )}
        </ImageWrapper>
    );
};

const ImageWrapper = styled.div`
    position: relative;
    overflow: hidden;
`;
const ImageContainer = styled.div`
    width: ${({ listingType }) => listingType && listingType.width};
    height: ${({ listingType }) => listingType && listingType.height};
    position: relative;
    overflow: hidden;

    img {
        width: 100%;
        height: 100%;
        object-fit: cover;
    }
`;
const Dots = styled.div`
    display: flex;
    gap: 10px;
    position: absolute;
    justify-content: center;
    align-items: center;
    overflow: auto;
    bottom: 15px;
    left: 15px;

    ::-webkit-scrollbar {
        display: none;
    }
`;
const Dot = styled.button`
    border: none;
    width: ${({ isActive }) => (isActive ? '10px' : '8px')};
    height: ${({ isActive }) => (isActive ? '10px' : '8px')};
    min-width: ${({ isActive }) => (isActive ? '10px' : '8px')};
    min-height: ${({ isActive }) => (isActive ? '10px' : '8px')};
    padding: 2px;
    background: ${({ isActive, theme }) =>
        isActive ? `${theme.colors.primary}` : 'rgba(190, 190, 190, 0.5)'};
    border-radius: 50%;
    cursor: pointer;
    position: relative;
`;
export default ProductCardSlider;
